<template>
  <MenuListItem
    :title="displayTitle"
    :to="instanceUrl"
    :icon="currentModel.icon && !instance.parentId ? currentModel.icon : undefined"
    :subtitle="subtitle"
    :badge="undefined"
    :custom-classes="[
      'instance-menu-item',
      { 'animate-wiggle': instance.status.shouldAnimate },
      {
        'text-weight-medium': !props.isChild,
        'text-weight-regular': props.isChild,
      },
    ]"
    :title-class="instanceClass + 'text-capitalize text-weight-medium'" />
</template>

<script setup lang="ts">
import useCrudUtils from 'src/CrudManager/Core/composables/CrudUtils/useCrudUtils'
import MenuListItem from 'src/CrudManager/Core/composites/Layout/MenuListItem.vue'
import type { CrudInstance, CrudSubtitle } from 'src/CrudManager/Core/types/AdvancedCrudManager'
import { computed, toRef } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  instance: CrudInstance
  isChild?: boolean
  hasVisibleChildren?: boolean
}>()

const { generateModuleUrl, getModelMeta } = useCrudUtils()

// Create reactive references to instance properties
const instanceRef = toRef(props, 'instance')

const currentModel = computed(() => {
  const modelMeta = getModelMeta(instanceRef.value.modelName)
  return {
    icon: modelMeta?.icon,
    category: modelMeta?.category,
    name: modelMeta?.name,
  }
})

const { t } = useI18n()

const displayTitle = computed(() => {
  return instanceRef.value.parentId ? instanceRef.value.title : t(`crudmodels.${instanceRef.value.modelName}.name`, 2)
})

const subtitle = computed<CrudSubtitle | null>(() => {
  return instanceRef.value.subtitle
})

const instanceClass = computed(() => {
  return instanceRef.value.instanceClass || ''
})

const instanceUrl = computed(() => {
  const url = generateModuleUrl(instanceRef.value.modelName, instanceRef.value.moduleName, instanceRef.value.props?.modelId as string)
  return url || '#' // Return '#' as a fallback if the URL is empty
})
</script>

<style lang="scss" scoped>
.icon-instance {
  border: 1px solid $blue;
  border-radius: 20%;
}

@keyframes wiggle {
  0% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(4px);
  }
  75% {
    transform: translateX(-4px);
  }
  100% {
    transform: translateX(0);
  }
}

.instance-menu-item {
  transition: background-color 0.3s ease;

  &.animate-wiggle {
    animation: wiggle 0.5s ease;
  }
}

.q-item-label {
  line-height: 1.2;
}

.q-badge {
  font-size: 0.75rem;
}
</style>
